<template>
  <div>
    <nav class="navbar">
      <button class="mobile-menu-toggle" @click="toggleMobileMenu" :class="{ active: mobileMenuOpen }" aria-label="Toggle menu">
        <span class="hamburger-line"></span>
        <span class="hamburger-line"></span>
        <span class="hamburger-line"></span>
      </button>
      <div class="navbar-brand">
        <a href="#/" @click.prevent="handleNavClick('#')">Brasse PC</a>
      </div>
      <div class="navbar-nav" :class="{ open: mobileMenuOpen }">
        <a href="#/" :class="{ active: currentPath === '#' }" @click.prevent="handleNavClick('#')">
          <span class="nav-icon">🏠</span><span class="nav-text">Home</span>
        </a>
        <a href="#/about" :class="{ active: currentPath === '#/about' }" @click.prevent="handleNavClick('#/about')">
          <span class="nav-icon">ℹ️</span><span class="nav-text">About</span>
        </a>
        <a href="#/server" :class="{ active: currentPath === '#/server' }" @click.prevent="handleNavClick('#/server')">
          <span class="nav-icon">🖥️</span><span class="nav-text">Server</span>
        </a>
        <a href="#/wishlist" :class="{ active: currentPath === '#/wishlist' }" @click.prevent="handleNavClick('#/wishlist')">
          <span class="nav-icon">🎁</span><span class="nav-text">Wishlist</span>
        </a>
        <a href="#/runestone" :class="{ active: currentPath === '#/runestone' }" @click.prevent="handleNavClick('#/runestone')">
          <span class="nav-icon">🗿</span><span class="nav-text">Runsten</span>
        </a>
        <a href="#/gymtracker" :class="{ active: currentPath === '#/gymtracker' }" @click.prevent="handleNavClick('#/gymtracker')">
          <span class="nav-icon">🏋️</span><span class="nav-text">GymTracker</span>
        </a>
      </div>
      <div v-if="mobileMenuOpen" class="mobile-menu-overlay" :class="{ show: mobileMenuOpen }" @click="closeMobileMenu"></div>
    </nav>
    <main class="main-content">
      <component :is="currentView" />
    </main>
  </div>
</template>

<script>
import { ref, computed } from 'vue'
import ServerView from './views/ServerView.vue'
import WishListView from './views/WishListView.vue'
import AboutView from './views/AboutView.vue'
import HomeView from './views/HomeView.vue'
import RunestoneView from './views/RunestoneView.vue'
import GymTrackerView from './views/GymTrackerView.vue'
import VerifyView from './views/VerifyView.vue'

export default {
  name: 'App',
  components: {
    HomeView,
    AboutView,
    ServerView,
    WishListView,
    RunestoneView,
    GymTrackerView,
    VerifyView
  },
  setup() {
    const routes = {
      '/': HomeView,
      '/about': AboutView,
      '/server': ServerView,
      '/wishlist': WishListView,
      '/runestone': RunestoneView,
      '/gymtracker': GymTrackerView,
      '/verify': VerifyView,
    }

    const currentPath = ref(window.location.hash)
    const mobileMenuOpen = ref(false)

    window.addEventListener('hashchange', () => {
      currentPath.value = window.location.hash
      mobileMenuOpen.value = false // Close mobile menu on route change
    })

    const currentView = computed(() => {
      // Strippa ev. query-sträng (t.ex. #/verify?token=abc → /verify)
      const path = currentPath.value.slice(1).split('?')[0] || '/'
      return routes[path] || AboutView
    })

    const setActivePath = (path) => {
      currentPath.value = path
    }

    const toggleMobileMenu = () => {
      mobileMenuOpen.value = !mobileMenuOpen.value
    }

    const closeMobileMenu = () => {
      mobileMenuOpen.value = false
    }

    const handleNavClick = (path) => {
      setActivePath(path)
      closeMobileMenu()
      window.location.hash = path
    }

    return {
      currentView,
      currentPath,
      setActivePath,
      mobileMenuOpen,
      toggleMobileMenu,
      closeMobileMenu,
      handleNavClick
    }
  }
}
</script>

<style>
:root {
  --color-primary: #2e7d32; /* primary accent (green) */
  --color-background: #ffffff;
  --color-border: #ececec;
  --color-text: #2c3e50;
  --color-heading: #222222;
  --color-on-primary: #ffffff;
  --navbar-height: 70px;
}

/* Global reset */
* {
  box-sizing: border-box;
}

html, body {
  margin: 0;
  padding: 0;
  height: 100%;
  overflow-x: hidden;
}

/* Navbar styles */
.navbar {
  background-color: #333;
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: var(--navbar-height);
  z-index: 1000;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 1rem;
}

.navbar-brand {
  font-size: 1.5rem;
  font-weight: bold;
  color: white;
  z-index: 1002;
  position: relative;
}

.navbar-brand a {
  color: white;
  text-decoration: none;
  transition: color 0.3s ease;
}

.navbar-brand a:hover {
  color: var(--color-primary);
}

/* Desktop navigation */
.navbar-nav {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.navbar-nav a {
  color: white;
  text-decoration: none;
  padding: 0.75rem 1rem;
  border-radius: 6px;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 500;
  position: relative;
}

.navbar-nav a:hover {
  background-color: rgba(255, 255, 255, 0.1);
  transform: translateY(-2px);
}

.navbar-nav a.active {
  background-color: var(--color-primary);
  box-shadow: 0 2px 8px rgba(46, 125, 50, 0.3);
}

.nav-icon {
  font-size: 1.1rem;
}

.nav-text {
  font-size: 1rem;
}

/* Mobile menu toggle button */
.mobile-menu-toggle {
  display: none;
  flex-direction: column;
  justify-content: space-around;
  width: 30px;
  height: 30px;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
  z-index: 1002;
  position: relative;
  transition: transform 0.3s ease;
}

.mobile-menu-toggle.active {
  transform: rotate(180deg);
}

.hamburger-line {
  width: 100%;
  height: 3px;
  background-color: white;
  border-radius: 2px;
  transition: all 0.3s ease;
  transform-origin: center;
}

.mobile-menu-toggle.active .hamburger-line:nth-child(1) {
  transform: rotate(45deg) translate(6px, 6px);
}

.mobile-menu-toggle.active .hamburger-line:nth-child(2) {
  opacity: 0;
}

.mobile-menu-toggle.active .hamburger-line:nth-child(3) {
  transform: rotate(-45deg) translate(6px, -6px);
}

/* Mobile menu overlay */
.mobile-menu-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 998;
  opacity: 0;
  visibility: hidden;
  transition: all 0.3s ease;
}

.mobile-menu-overlay.show {
  opacity: 1;
  visibility: visible;
}

/* Main content */
.main-content {
  margin-top: var(--navbar-height);
  min-height: calc(100vh - var(--navbar-height));
}

/* App styles */
#app {
  font-family: Avenir, Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: #2c3e50;
  height: 100vh;
  overflow-y: auto;
}

/* Mobile responsive styles */
@media (max-width: 768px) {
  .navbar {
    padding: 0 1rem;
  }

  .mobile-menu-toggle {
    display: flex;
  }

  .navbar-nav {
    position: fixed;
    top: var(--navbar-height);
    left: 0;
    width: 100%;
    height: calc(100vh - var(--navbar-height));
    background-color: #333;
    flex-direction: column;
    justify-content: flex-start;
    align-items: stretch;
    padding: 2rem 0;
    gap: 0;
    transform: translateX(-100%);
    transition: transform 0.3s ease;
    overflow-y: auto;
    z-index: 999;
  }

  .navbar-nav.open {
    transform: translateX(0);
  }

  .navbar-nav a {
    padding: 1.25rem 2rem;
    border-radius: 0;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    justify-content: flex-start;
    font-size: 1.1rem;
    color: white;
    opacity: 1;
    pointer-events: auto;
    user-select: none;
    -webkit-user-select: none;
  }

  .navbar-nav a:hover {
    background-color: rgba(255, 255, 255, 0.15);
    transform: none;
  }

  .navbar-nav a:active {
    background-color: rgba(255, 255, 255, 0.2);
  }

  .navbar-nav a.active {
    background-color: var(--color-primary);
    border-left: 4px solid #4caf50;
    color: white;
    opacity: 1;
  }

  .nav-icon {
    font-size: 1.3rem;
    min-width: 1.5rem;
  }

  .nav-text {
    font-size: 1.1rem;
  }

  .mobile-menu-overlay {
    opacity: 1;
    visibility: visible;
  }

  /* Hide brand text on very small screens */
  @media (max-width: 480px) {
    .navbar-brand {
      font-size: 1.2rem;
    }
  }
}

/* Tablet styles */
@media (max-width: 1024px) and (min-width: 769px) {
  .navbar-nav {
    gap: 0.5rem;
  }
  
  .navbar-nav a {
    padding: 0.5rem 0.75rem;
    font-size: 0.95rem;
  }
  
  .nav-icon {
    font-size: 1rem;
  }
}

/* Touch device improvements */
@media (hover: none) and (pointer: coarse) {
  .navbar-nav a {
    min-height: 48px; /* Minimum touch target size */
    display: flex;
    align-items: center;
  }
  
  .mobile-menu-toggle {
    min-width: 48px;
    min-height: 48px;
  }
}
</style>
